import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import { API_BASE_URL } from "../config/api";

const ProductDetails = () => {
  const { lang } = useLanguage();
  const { brand, model } = useParams();

  const [products, setProducts] = useState([]);
  const [mainProduct, setMainProduct] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [mode, setMode] = useState(null);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    name: "",
    surname: "",
    mobile: "",
    address: "",
  });

  const colorMap = {
    Zeze: "#000000",
    Bardhe: "#ffffff",
    Gri: "#9ca3af",
    Blu: "#2563eb",
    Jeshile: "#16a34a",
    Kuqe: "#dc2626",
    Verdhe: "#eab308",
  };

  // ================= TRANSLATIONS =================
  const t = {
    sq: {
      memory: "Memorje",
      battery: "Bateri",
      color: "Ngjyra",
      condition: "Gjendja",
      inStock: "Në stok",
      outStock: "S’ka stok",
      whatsapp: "WhatsApp",
      reserve: "Rezervo",
      reserveTitle: "Rezervo Produktin",
      confirm: "Konfirmo",
      close: "Mbyll",
      loading: "Duke dërguar...",
      error: "Plotëso emrin dhe numrin e telefonit!",
      success: "✔ Rezervimi u dërgua me sukses!",
      fail: "❌ Gabim gjatë rezervimit",
      noDesc: "Pa përshkrim",
      name: "Emri",
      surname: "Mbiemri",
      phone: "Telefoni",
      address: "Adresa",
    },
    en: {
      memory: "Memory",
      battery: "Battery",
      color: "Color",
      condition: "Condition",
      inStock: "In stock",
      outStock: "Out of stock",
      whatsapp: "WhatsApp",
      reserve: "Reserve",
      reserveTitle: "Reserve Product",
      confirm: "Confirm",
      close: "Close",
      loading: "Sending...",
      error: "Fill name and phone number!",
      success: "✔ Reservation sent successfully!",
      fail: "❌ Reservation error",
      noDesc: "No description",
      name: "Name",
      surname: "Surname",
      phone: "Phone",
      address: "Address",
    },
  };

  const text = t[lang] || t.sq;

  // ================= FETCH =================
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/products/group/${brand}/${model}`,
        );
        const data = await res.json();

        setProducts(data || []);
        setMainProduct(data?.[0] || null);
        setSelectedId(data?.[0]?.id || null);
      } catch (err) {
        console.error(err);
      }
    };

    fetchData();
  }, [brand, model]);

  const selectProduct = (p) => {
    setMainProduct(p);
    setSelectedId(p.id);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // ================= WHATSAPP =================
  const handleWhatsApp = () => {
    if (!mainProduct) return;

    const phone = "355674909765";

    const msg =
      lang === "en"
        ? `Hello 👋

I am interested in this product:

📱 Name: ${mainProduct.name}
🏷 Brand: ${mainProduct.brand}
📌 Model: ${mainProduct.model}
💰 Price: €${mainProduct.price}
📦 Status: ${mainProduct.stock > 0 ? "In stock" : "Out of stock"}

Thank you 🙏`
        : `Përshëndetje 👋

Jam i interesuar për këtë produkt:

📱 Emri: ${mainProduct.name}
🏷 Marka: ${mainProduct.brand}
📌 Modeli: ${mainProduct.model}
💰 Çmimi: €${mainProduct.price}
📦 Gjendja: ${mainProduct.stock > 0 ? "Në stok" : "S’ka stok"}

Faleminderit 🙏`;

    window.open(
      `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`,
      "_blank",
    );
  };

  // ================= RESERVE =================
  const handleReserve = async () => {
    if (!mainProduct) return;

    if (!form.name || !form.mobile) {
      alert(text.error);
      return;
    }

    try {
      setLoading(true);

      const res = await fetch(`${API_BASE_URL}/api/orders/reserve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          surname: form.surname,
          mobile: form.mobile,
          address: form.address,
          email: null,
          product: mainProduct,
        }),
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.error);

      alert(text.success);

      setForm({
        name: "",
        surname: "",
        mobile: "",
        address: "",
      });

      setMode(null);
    } catch (err) {
      console.error(err);
      alert(text.fail);
    } finally {
      setLoading(false);
    }
  };

  if (!mainProduct) return <div className="p-10 text-center">Loading...</div>;

  return (
    <div className="max-w-6xl mx-auto p-6 grid md:grid-cols-2 gap-10">
      {/* IMAGE */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-lg p-6 flex flex-col items-center"
      >
        <img
          src={`${API_BASE_URL}/uploads/${mainProduct.images}`}
          className="w-80 object-contain"
          alt=""
        />

        <div className="flex gap-3 mt-6">
          {products.map((p) => (
            <div
              key={p.id}
              onClick={() => selectProduct(p)}
              className={`w-9 h-9 rounded-full cursor-pointer border-2 ${
                selectedId === p.id
                  ? "scale-110 border-black"
                  : "border-gray-300"
              }`}
              style={{ backgroundColor: colorMap[p.color] || "#ddd" }}
            />
          ))}
        </div>
      </motion.div>

      {/* INFO */}
      <div className="flex flex-col justify-between">
        <div>
          <h1 className="text-2xl font-bold">{mainProduct.name}</h1>
          <p className="text-gray-500">
            {mainProduct.brand} • {mainProduct.model}
          </p>

          <div className="grid grid-cols-2 gap-3 text-sm mt-4">
            <div>
              <b>{text.memory}:</b> {mainProduct.memory}
            </div>
            <div>
              <b>{text.battery}:</b> {mainProduct.battery || "-"}
            </div>
            <div>
              <b>{text.color}:</b> {mainProduct.color}
            </div>
            <div>
              <b>{text.condition}:</b> {mainProduct.product_condition}
            </div>
          </div>

          <div className="text-2xl font-bold text-green-600 mt-4">
            €{mainProduct.price}
          </div>

          <div className="mt-2">
            {mainProduct.stock > 0 ? (
              <span className="text-green-600">✔ {text.inStock}</span>
            ) : (
              <span className="text-red-600">✖ {text.outStock}</span>
            )}
          </div>

          <p className="mt-4 text-gray-600">
            {mainProduct.description || text.noDesc}
          </p>
        </div>

        {/* BUTTONS */}
        <div className="flex gap-4 mt-6">
          <button
            onClick={handleWhatsApp}
            className="flex-1 bg-green-600 text-white py-3 rounded-xl"
          >
            {text.whatsapp}
          </button>

          <button
            onClick={() => setMode("reserve")}
            className="flex-1 bg-purple-600 text-white py-3 rounded-xl"
          >
            {text.reserve}
          </button>
        </div>
      </div>

      {/* MODAL */}
      {mode === "reserve" && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-2xl w-[350px]">
            <h2 className="font-bold mb-4">{text.reserveTitle}</h2>

            <input
              name="name"
              placeholder={text.name}
              value={form.name}
              onChange={handleChange}
              className="border p-2 w-full mb-2 rounded"
            />

            <input
              name="surname"
              placeholder={text.surname}
              value={form.surname}
              onChange={handleChange}
              className="border p-2 w-full mb-2 rounded"
            />

            <input
              name="mobile"
              placeholder={text.phone}
              value={form.mobile}
              onChange={handleChange}
              className="border p-2 w-full mb-2 rounded"
            />

            <textarea
              name="address"
              placeholder={text.address}
              value={form.address}
              onChange={handleChange}
              className="border p-2 w-full mb-2 rounded"
            />

            <button
              disabled={loading}
              onClick={handleReserve}
              className="bg-blue-600 text-white w-full py-2 rounded-lg"
            >
              {loading ? text.loading : text.confirm}
            </button>

            <button
              onClick={() => setMode(null)}
              className="text-red-500 w-full mt-2"
            >
              {text.close}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetails;
